import { Box, Card, Group, Image, Overlay, Stack, Text } from '@mantine/core'
import { useHover } from '@mantine/hooks'
import ImagePlaceholder from '@/assets/datasets/dataset.png'
import { LabelingTypeChip } from '@/components/annotations/LabelingTypes/LabelingTypeChip'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'
import { DatasetType, LabelingType } from '@/types/dataset'
import { DatasetTypeChip } from '../DatasetTypeChip/DatasetTypeChip'
import { FileCountLabel } from './FileCountLabel'
import { PreviewButton } from './PreviewButton'

const IMAGE_HEIGHT = 110

type DatasetCardProps = {
  name: string
  thumbnailUrl?: string
  fileCount: number
  datasetType: DatasetType
  labelingType: LabelingType
  isSelected: boolean
  disabled?: boolean
  onSelect: () => void
  onPreview: () => void
}

export const DatasetCard = ({
  name,
  thumbnailUrl,
  fileCount,
  datasetType,
  labelingType,
  isSelected,
  disabled,
  onSelect,
  onPreview
}: DatasetCardProps) => {
  const { hovered, ref } = useHover()

  const imageStyles = {
    root: {
      filter: isSelected ? 'none' : 'blur(1px)',
      opacity: isSelected ? 1 : 0.6
    }
  }

  return (
    <SelectableCard
      height={220}
      renderAs="div"
      isSelected={isSelected}
      disabled={disabled}
      onClick={onSelect}
    >
      <Card.Section ref={ref} withBorder>
        <Box pos="relative" bg="gray.0">
          <Image
            h={IMAGE_HEIGHT}
            fit="cover"
            src={thumbnailUrl || ImagePlaceholder}
            fallbackSrc={ImagePlaceholder}
            styles={imageStyles}
          />

          {hovered && !disabled && (
            <Overlay color="black" backgroundOpacity={0.5} blur={50} center>
              <PreviewButton
                onClick={(e) => {
                  e.stopPropagation()
                  onPreview()
                }}
              />

              <Box pos="absolute" bottom={0} right={0}>
                <FileCountLabel fileCount={fileCount} />
              </Box>
            </Overlay>
          )}
        </Box>
      </Card.Section>

      <Card.Section py="xs" inheritPadding>
        <Stack align="center" gap="xs">
          <Text
            size="sm"
            ta="center"
            fw="bold"
            style={{
              userSelect: 'none'
            }}
            lineClamp={2}
          >
            {name}
          </Text>

          <Group gap={4} justify="center">
            <LabelingTypeChip labelingType={labelingType} />

            {datasetType === DatasetType.Secondary && (
              <DatasetTypeChip datasetType={datasetType} />
            )}
          </Group>
        </Stack>
      </Card.Section>
    </SelectableCard>
  )
}
